import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Link2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api, ApiError, type ShippingMethod } from "@/lib/api";
import { Button, Input } from "./ui";

import { t, msg } from "@/lib/i18n";
const EXAMPLE = { sendungsnummer: "00340434161094042557", plz: "10115", land: "DE" };
const fill = (t: string) => t.replaceAll("{sendungsnummer}", EXAMPLE.sendungsnummer).replaceAll("{plz}", EXAMPLE.plz).replaceAll("{land}", EXAMPLE.land);

/** Vorschläge der gängigen Dienstleister – genauere zuerst (Warenpost/Express vor DHL Paket); vor dem Speichern über den Beispiel-Link prüfen */
const PRESETS: { label: string; match: RegExp; carrierName: string; url: string }[] = [
  { label: msg("Deutsche Post / Warenpost"), match: /post|warenpost/i, carrierName: "Deutsche Post", url: "https://www.dhl.de/de/privatkunden/pakete-empfangen/verfolgen.html?piececode={sendungsnummer}" },
  { label: msg("DHL Express"), match: /express/i, carrierName: "DHL Express", url: "https://www.dhl.com/de-de/home/tracking/tracking-express.html?submit=1&tracking-id={sendungsnummer}" },
  { label: msg("DHL Paket"), match: /dhl(?!.*express)|paket/i, carrierName: "DHL Paket", url: "https://www.dhl.de/de/privatkunden/pakete-empfangen/verfolgen.html?piececode={sendungsnummer}" },
  { label: msg("DPD"), match: /dpd/i, carrierName: "DPD", url: "https://tracking.dpd.de/status/de_DE/parcel/{sendungsnummer}" },
  { label: msg("GLS"), match: /gls/i, carrierName: "GLS", url: "https://gls-group.com/DE/de/paketverfolgung?match={sendungsnummer}" },
  { label: msg("UPS"), match: /ups/i, carrierName: "UPS", url: "https://www.ups.com/track?loc=de_DE&tracknum={sendungsnummer}" },
  { label: msg("Hermes"), match: /hermes/i, carrierName: "Hermes", url: "https://www.myhermes.de/empfangen/sendungsverfolgung/sendungsinformation/#{sendungsnummer}" },
  { label: msg("FedEx"), match: /fedex/i, carrierName: "FedEx", url: "https://www.fedex.com/fedextrack/?trknbr={sendungsnummer}" },
];

/** Gleiche Prüfung wie im Server – sofortige Rückmeldung beim Tippen */
function templateError(t: string): string | null {
  if (!t) return null;
  if (!/^https:\/\/\S+$/i.test(t)) return "Muss mit https:// beginnen und darf keine Leerzeichen enthalten.";
  if (!t.includes("{sendungsnummer}")) return "{sendungsnummer} fehlt.";
  const unknown = (t.match(/\{[^}]*\}/g) ?? []).filter((p) => !["{sendungsnummer}", "{plz}", "{land}"].includes(p));
  return unknown.length ? `Unbekannter Platzhalter: ${unknown.join(", ")}` : null;
}

/** Logiful-Admin: Dienstleister und Tracking-Link einer Versandart, wie sie an Shopify gehen (gilt für alle Kunden) */
export function ShippingTrackingDialog({ method, open, onOpenChange }: { method: ShippingMethod; open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const [carrierName, setCarrierName] = useState(method.tracking?.carrierName ?? "");
  const [urlTemplate, setUrlTemplate] = useState(method.tracking?.urlTemplate ?? "");
  const err = templateError(urlTemplate.trim());
  const save = useMutation({
    mutationFn: () => api.admin.setShippingMethodTracking(method.id, { carrierName: carrierName.trim() || null, urlTemplate: urlTemplate.trim() || null }),
    onSuccess: () => {
      toast.success("Tracking gespeichert", { description: msg("Gilt für alle ab jetzt gemeldeten Sendungen dieser Versandart – bei allen Kunden.") });
      void qc.invalidateQueries({ queryKey: ["shipping-methods"] });
      onOpenChange(false);
    },
    onError: (e) => toast.error("Nicht gespeichert", { description: e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "") : "" }),
  });
  const insert = (p: string) => setUrlTemplate((v) => v + p);
  // passender Vorschlag zuerst (nach Dienstleister/Name der Versandart)
  const hint = `${method.carrier ?? ""} ${method.name}`;
  const presets = [...PRESETS].sort((a, b) => Number(b.match.test(hint)) - Number(a.match.test(hint)));

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/30 animate-fade-in" />
        <Dialog.Content className="fixed z-[70] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(560px,calc(100vw-32px))] rounded-2xl border border-border bg-surface p-6 shadow-2xl animate-pop-in">
          <Dialog.Title className="text-[17px] font-semibold flex items-center gap-2"><Link2 className="size-4 text-fg-subtle" /> {t("Tracking an Shopify")}</Dialog.Title>
          <Dialog.Description className="mt-1 text-[13px] text-fg-muted">
            {method.name}{method.carrier ? ` · ${method.carrier}` : ""} {t("– so geht jede Sendung dieser Versandart an Shopify, statt Shopify die Zuordnung zu überlassen. Gilt für alle Kunden.")}
          </Dialog.Description>

          <div className="mt-4 flex flex-wrap items-center gap-1.5 text-[12px]">
            <span className="text-fg-subtle">{t("Vorschlag übernehmen:")}</span>
            {presets.map((p, i) => (
              <button key={p.label} type="button" onClick={() => { setCarrierName(p.carrierName); setUrlTemplate(p.url); }} className={i === 0 && p.match.test(hint) ? "rounded-full border border-fg/30 bg-surface-2 px-2.5 py-0.5 font-medium" : "rounded-full border border-border px-2.5 py-0.5 text-fg-muted hover:text-fg hover:bg-surface-2"}>
                {t(p.label)}
              </button>
            ))}
          </div>

          <label className="block mt-4">
            <span className="text-[12px] font-medium text-fg-muted">{t("Dienstleister in Shopify")}</span>
            <Input className="mt-1" value={carrierName} onChange={(e) => setCarrierName(e.target.value)} placeholder={method.carrier ?? t("z. B. DHL Paket")} maxLength={60} />
            <span className="block mt-1 text-[11.5px] text-fg-subtle">{t("Wird unverändert übertragen und dem Endkunden angezeigt. Leer = automatische Zuordnung aus „")}{method.carrier ?? t("Dienstleister des Lagers")}“.</span>
          </label>

          <label className="block mt-4">
            <span className="text-[12px] font-medium text-fg-muted">{t("Tracking-Link")}</span>
            <Input className="mt-1 font-mono text-[12.5px]" value={urlTemplate} onChange={(e) => setUrlTemplate(e.target.value)} invalid={!!err} placeholder={t("https://www.dhl.de/de/privatkunden/pakete-empfangen/verfolgen.html?piececode={sendungsnummer}")} spellCheck={false} />
          </label>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11.5px] text-fg-subtle">
            {t("Platzhalter einfügen:")}
            {["{sendungsnummer}", "{plz}", "{land}"].map((p) => (
              <button key={p} type="button" onClick={() => insert(p)} className="rounded-md border border-border px-1.5 py-0.5 font-mono hover:bg-surface-2 hover:text-fg">{p}</button>
            ))}
          </div>
          {err ? (
            <p className="mt-2 text-[12px] text-danger">{err}</p>
          ) : urlTemplate.trim() ? (
            <div className="mt-3 rounded-lg bg-surface-2/70 px-3 py-2 text-[12px]">
              <p className="text-fg-subtle">{t("Beispiel (Sendung")} {EXAMPLE.sendungsnummer}{t(", PLZ")} {EXAMPLE.plz}):</p>
              <a href={fill(urlTemplate.trim())} target="_blank" rel="noreferrer" className="font-mono break-all hover:underline inline-flex items-start gap-1">
                {fill(urlTemplate.trim())}<ExternalLink className="size-3 shrink-0 mt-0.5" />
              </a>
            </div>
          ) : (
            <p className="mt-2 text-[11.5px] text-fg-subtle">{t("Leer = Link des Lagers bzw. Shopify erzeugt ihn selbst.")}</p>
          )}

          <div className="mt-6 flex justify-end gap-2">
            <Dialog.Close asChild><Button variant="ghost">{t("Abbrechen")}</Button></Dialog.Close>
            <Button variant="primary" disabled={!!err} loading={save.isPending} onClick={() => save.mutate()}>{t("Speichern")}</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
